import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import openaiService from '../services/openaiService';

const { FiTool, FiLoader, FiAlertCircle } = FiIcons;

const ServiceAdvisor = () => {
  const [vehicleInfo, setVehicleInfo] = useState({
    model: '',
    year: '',
    mileage: ''
  });
  const [issues, setIssues] = useState('');
  const [advice, setAdvice] = useState('');
  const [loading, setLoading] = useState(false);

  const audiModels = ['Q3', 'Q5', 'Q7', 'Q8', 'A3', 'A4', 'A6', 'A8', 'e-tron', 'R8'];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 15 }, (_, i) => currentYear - i);

  const getServiceAdvice = async () => {
    if (!vehicleInfo.model || !vehicleInfo.year || !issues.trim()) {
      alert('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const vehicleString = `${vehicleInfo.year} Audi ${vehicleInfo.model}${vehicleInfo.mileage ? ` with ${vehicleInfo.mileage} miles` : ''}`;
      const result = await openaiService.generateServiceAdvice(vehicleString, issues);
      setAdvice(result);
    } catch (error) {
      setAdvice('For the best service recommendations, please schedule an appointment with our certified Audi technicians who can properly diagnose your vehicle.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-800 rounded-lg p-6 border border-gray-700"
    >
      <h3 className="text-xl font-bold text-white mb-4 flex items-center">
        <SafeIcon icon={FiTool} className="w-5 h-5 mr-2 text-audi-red" />
        AI Service Advisor
      </h3>

      <div className="space-y-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Audi Model *
            </label>
            <select
              value={vehicleInfo.model}
              onChange={(e) => setVehicleInfo({...vehicleInfo, model: e.target.value})}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-audi-red"
            >
              <option value="">Select Model</option>
              {audiModels.map((model) => (
                <option key={model} value={model}>{model}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Year *
            </label>
            <select
              value={vehicleInfo.year}
              onChange={(e) => setVehicleInfo({...vehicleInfo, year: e.target.value})}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-audi-red"
            >
              <option value="">Select Year</option>
              {years.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Mileage (Optional)
            </label>
            <input
              type="number"
              value={vehicleInfo.mileage}
              onChange={(e) => setVehicleInfo({...vehicleInfo, mileage: e.target.value})}
              placeholder="e.g., 45000"
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-audi-red"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Describe Issues or Maintenance Needs *
          </label>
          <textarea
            value={issues}
            onChange={(e) => setIssues(e.target.value)}
            placeholder="Describe any issues, sounds, warning lights, or maintenance questions you have..."
            rows="4"
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-audi-red resize-none"
          />
        </div>
      </div>

      <button
        onClick={getServiceAdvice}
        disabled={loading}
        className="w-full bg-audi-red hover:bg-red-700 disabled:bg-gray-600 text-white py-3 px-4 rounded-lg font-semibold transition-colors flex items-center justify-center"
      >
        {loading ? (
          <>
            <SafeIcon icon={FiLoader} className="w-4 h-4 mr-2 animate-spin" />
            Analyzing...
          </>
        ) : (
          'Get Service Advice'
        )}
      </button>

      {advice && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 p-4 bg-gray-700 rounded-lg"
        >
          <h4 className="font-bold text-white mb-3 flex items-center">
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5 mr-2 text-audi-red" />
            Service Recommendations
          </h4>
          <div className="text-gray-200 whitespace-pre-wrap text-sm leading-relaxed mb-4">
            {advice}
          </div>
          <button className="w-full bg-audi-red hover:bg-red-700 text-white py-2 px-4 rounded-lg transition-colors">
            Schedule Service Appointment
          </button>
        </motion.div>
      )}
    </motion.div>
  );
};

export default ServiceAdvisor;